import React from 'react';
import { RotateCcw, ArrowLeft } from 'lucide-react';
import useGameStore, { getOffice } from '../../game/gameStore';

const OUTCOMES = {
  exposed: { title: 'Escândalo!', subtitle: 'Sua carreira política acabou em desonra.', color: '#f87171' },
  irrelevant: { title: 'Esquecido...', subtitle: 'Sem apoio popular, você se tornou irrelevante.', color: '#f59e0b' },
  presidente: { title: 'Presidente da República!', subtitle: 'Você alcançou o cargo mais alto do país.', color: '#4ade80' },
  fichaLimpa: { title: 'Ficha Suja', subtitle: 'A Lei da Ficha Limpa encerrou sua carreira.', color: '#f87171' },
  bureaucrat: { title: 'Burocrata', subtitle: 'Uma carreira sem glória na máquina pública.', color: '#6b7280' },
};

export default function GameOverScreen({ onPlayAgain, onRestart }) {
  const outcome = useGameStore(s => s.outcome);
  const player = useGameStore(s => s.player);
  const careerPath = useGameStore(s => s.careerPath);
  const currentOffice = useGameStore(s => s.currentOffice);
  const territories = useGameStore(s => s.territories);
  const playerParty = useGameStore(s => s.playerParty);
  const coalitionParties = useGameStore(s => s.coalitionParties);
  const round = useGameStore(s => s.round);

  const info = OUTCOMES[outcome] || OUTCOMES.bureaucrat;
  const office = getOffice(currentOffice);

  const alliedParties = [playerParty, ...coalitionParties];
  const totalT = Object.keys(territories).length;
  const controlledT = Object.values(territories).filter(t => alliedParties.includes(t.controlledBy)).length;
  const controlPct = totalT > 0 ? Math.round((controlledT / totalT) * 100) : 0;

  const pathStr = careerPath.map(id => { const o = getOffice(id); return `${o.emoji} ${o.name}`; }).join(' → ');

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: outcome === 'presidente' ? 'rgba(2,16,8,0.9)' : 'rgba(16,2,2,0.9)' }}>
      <div className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl overflow-hidden max-h-[95vh] sm:max-h-[90vh] flex flex-col"
        style={{ background: '#0b1424', border: `1px solid ${info.color}33` }}>
        <div className="h-1.5 w-full shrink-0" style={{ background: info.color }} />

        <div className="p-5 sm:p-8 space-y-4 sm:space-y-5 text-center overflow-y-auto flex-1" style={{ scrollbarWidth: 'thin' }}>
          <h1 className="text-2xl sm:text-3xl font-black" style={{ color: info.color }}>{info.title}</h1>
          <p className="text-xs sm:text-sm" style={{ color: '#8899bb' }}>{info.subtitle}</p>

          <div className="py-2 sm:py-3">
            <p className="text-[9px] sm:text-[10px] uppercase tracking-widest mb-2" style={{ color: '#556688' }}>Trajetória Política</p>
            <p className="text-[10px] sm:text-xs" style={{ color: '#8899bb' }}>{pathStr}</p>
            <p className="text-[9px] sm:text-[10px] mt-1" style={{ color: '#445566' }}>
              {office.emoji} {office.name} · Rodada {round}
            </p>
          </div>

          <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
            {[
              { label: 'Carisma', value: `${player.popularity}%`, color: '#4ade80' },
              { label: 'Influência', value: `${player.influence}%`, color: '#a78bfa' },
              { label: 'Recursos', value: `R$${player.money}K`, color: '#fbbf24' },
              { label: 'Risco', value: `${player.risk}%`, color: '#f87171' },
            ].map(s => (
              <div key={s.label} className="p-1.5 sm:p-2 rounded-lg" style={{ background: '#0f1a30', border: '1px solid #1e3050' }}>
                <p className="text-[8px] sm:text-[10px]" style={{ color: '#556688' }}>{s.label}</p>
                <p className="text-xs sm:text-sm font-bold" style={{ color: s.color }}>{s.value}</p>
              </div>
            ))}
          </div>

          {totalT > 0 && (
            <div>
              <p className="text-[9px] sm:text-[10px]" style={{ color: '#556688' }}>Controle: {controlPct}% ({controlledT}/{totalT})</p>
              <div className="h-1.5 rounded-full mt-1 mx-auto max-w-xs" style={{ background: '#141e30' }}>
                <div className="h-full rounded-full" style={{ width: `${controlPct}%`, background: controlPct > 50 ? '#4ade80' : '#fbbf24' }} />
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center pt-2 sm:pt-3">
            <button onClick={onPlayAgain}
              className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold"
              style={{ background: '#14532d', color: '#4ade80', border: '1px solid #2a8040' }}>
              <RotateCcw size={14} /> Jogar Novamente
            </button>
            <button onClick={onRestart}
              className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold"
              style={{ background: '#1a1a30', color: '#6688aa', border: '1px solid #2a2a50' }}>
              <ArrowLeft size={14} /> Outra Cidade
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
